import classNames from 'classnames';
import type { ComponentProps, FC, ReactNode, JSX } from 'react';
import {
  forwardRef,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import PreFilledByAITooltip from '../PreFilledByAITooltip.component';
import { twMerge } from 'tailwind-merge';
import {
  useMaskInput,
  type Options as UseMaskInputOptions,
  Mask,
} from 'use-mask-input';

type Ref<T> =
  | React.RefObject<T | null>
  | ((ref: T | null) => void)
  | undefined
  | null;

type OriginalInputProps = Omit<
  JSX.IntrinsicElements['input'],
  'size' | 'ref' | 'type'
> & {
  ref?: Ref<any>;
};

type BaseInputRenderProps = {
  onFocus: () => void;
  onBlur: () => void;
  className: string;
};

type MaskProps = {
  mask: Mask;
  definitions?: UseMaskInputOptions['definitions'];
  placeholder?: UseMaskInputOptions['placeholder'];
};

export type BaseInputProps = OriginalInputProps & {
  type?: JSX.IntrinsicElements['input']['type'] | 'textarea';
  size?: 'xl' | 'base' | 'md' | 'sm' | 'xs' | 'xxs';
  Icon?: FC<ComponentProps<'svg'>> | false;
  LeftIcon?: FC<ComponentProps<'svg'>> | false;
  RightIcon?: FC<ComponentProps<'svg'>> | false;
  renderExtraLeft?: (className: string) => ReactNode;
  renderExtraRight?: (className: string) => ReactNode;
  error?: string | boolean;
  removeLeftRounding?: boolean;
  removeRightRounding?: boolean;
  containerClassName?: string;
  containerRef?: Ref<any>;
  renderInput?: (renderProps: BaseInputRenderProps) => ReactNode;
  iconClassName?: string;
  innerClassName?: string;
  aiTooltip?: {
    enabled?: boolean;
    description?: string | null;
  };
  extendContainerClassNames?: (
    options: Omit<ContainerOptions, 'getClassNames'>,
  ) => any;
  extendInputClassNames?: (options: Omit<InputOptions, 'getClassNames'>) => any;
  maskProps?: MaskProps;
};

// 1.5 + 1.25 + 1 = 3.75
const outerPaddingClasses = {
  xl: { l: 'pl-[1.25rem]', r: 'pr-[1.25rem]', t: 'pt-4.5', b: 'pb-4.5' },
  // large: { l: 'pl-[1rem]', r: 'pr-[1rem]', t: 'pt-4', b: 'pb-4' },
  base: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-3', b: 'pb-3' }, // 'px-2.5 py-3',
  md: { l: 'pl-[0.625rem]', r: 'pr-[0.625rem]', t: 'pt-2.5', b: 'pb-2.5' }, //'p-2.5'
  sm: { l: 'pl-[0.5rem]', r: 'pr-[0.5rem]', t: 'pt-2', b: 'pb-2' }, //'p-2',
  xs: { l: 'pl-[0.375rem]', r: 'pr-[0.375rem]', t: 'pt-1.5', b: 'pb-1.5' }, // 'p-1.5',
  xxs: { l: 'pl-[0.25rem]', r: 'pr-[0.25rem]', t: 'pt-1', b: 'pb-1' }, // 'p-1',
};

// left padding + icon width + icon right padding
const leftPaddingForInputWithIcon = {
  xl: 'pl-[3.75rem]',
  base: 'pl-[2.625rem]',
  md: 'pl-[2.625rem]',
  sm: 'pl-[2.5rem]',
  xs: 'pl-[2.25rem]',
  xxs: 'pl-[2rem]',
};

const rightPaddingForInputWithIcon = {
  xl: 'pr-[3.75rem]',
  base: 'pr-[2.625rem]',
  md: 'pr-[2.625rem]',
  sm: 'pr-[2.5rem]',
  xs: 'pr-[2.25rem]',
  xxs: 'pr-[2rem]',
};

// left indent for icons is same as left padding for input
const leftIndentForIcons = {
  xl: 'left-[1.25rem]',
  base: 'left-[0.625rem]',
  md: 'left-[0.625rem]',
  sm: 'left-[0.5rem]',
  xs: 'left-[0.375rem]',
  xxs: 'left-[0.25rem]',
};

const rightIndentForIcons = {
  xl: 'right-[1.25rem]',
  base: 'right-[0.625rem]',
  md: 'right-[0.625rem]',
  sm: 'right-[0.5rem]',
  xs: 'right-[0.375rem]',
  xxs: 'right-[0.25rem]',
};

type State = {
  // isHovered: boolean;
  isFocused: boolean;
  isDisabled: boolean;
  hasValue: boolean;
  hasError: boolean;
  size: BaseInputProps['size'];
};

type ContainerOptions = {
  state: State;
  useAIColorScheme?: boolean;
  getClassNames?: (options: Omit<ContainerOptions, 'getClassNames'>) => any;
};
const getContainerStateClasses = ({
  state,
  useAIColorScheme,
  getClassNames,
}: ContainerOptions) => {
  const { isFocused, isDisabled, hasValue, hasError } = state;

  const defaultShadow = 'shadow-[0px_1px_2px_0px_rgba(53,26,77,0.03)]';

  const externalClasses = getClassNames?.({ state, useAIColorScheme });

  if (isDisabled) {
    return twMerge(
      classNames(
        'bg-primary-50 cursor-not-allowed border',
        hasError ? 'border-[#EF4444]' : 'border-primary-100',
        externalClasses,
      ),
    );
  }

  if (hasError) {
    return twMerge(
      classNames(
        'relative z-[3] border border-[#Ef4444] bg-white',
        isFocused ? 'ring-2 ring-primary-100' : 'hover:bg-primary-50',
        useAIColorScheme ? '' : 'transition',
        externalClasses,
      ),
    );
  }

  if (isFocused) {
    return twMerge(
      classNames(
        'relative z-[3] bg-white border border-primary ring-2 ring-primary-100',
        useAIColorScheme ? '' : 'transition',
        externalClasses,
      ),
    );
  }

  return twMerge(
    classNames(
      defaultShadow,
      useAIColorScheme
        ? 'ai-input-bg-border'
        : 'bg-white transition hover:bg-primary-50 border border-primary-100',
      externalClasses,
    ),
  );
};

type InputOptions = {
  state: State;
  getClassNames?: (options: Omit<InputOptions, 'getClassNames'>) => any;
};
const getInputStateClasses = ({ state, getClassNames }: InputOptions) => {
  const { isFocused, isDisabled, hasValue, hasError, size } = state;

  const baseTextColor = 'text-primary';

  const externalClasses = getClassNames?.({ state });

  const generalClasses = classNames(
    'flex-1 min-w-0 text-xl md:text-base font-assistant !outline-none !border-0 transition bg-transparent !shadow-none',
    size === 'xl'
      ? 'text-xl md:text-lg font-semibold'
      : 'text-xl md:text-base leading-[1.5rem]',
  );

  if (isDisabled) {
    return twMerge(
      classNames(
        generalClasses,
        'placeholder-primary-400 text-primary',
        externalClasses,
      ),
    );
  }

  if (isFocused) {
    return twMerge(
      classNames(
        generalClasses,
        'placeholder-primary-400',
        baseTextColor,
        externalClasses,
      ),
    );
  }

  return twMerge(
    classNames(
      generalClasses,
      'placeholder-primary-400',
      baseTextColor,
      externalClasses,
    ),
  );
};

const getIconStateClasses = (state: State) => {
  const { isFocused, isDisabled, hasValue, hasError } = state;

  if (isDisabled) {
    return 'text-primary-200';
  }

  if (isFocused) {
    return 'text-primary';
  }

  if (hasValue) {
    return 'text-primary';
  }

  return 'text-primary-400';
};

const MaskedInput: FC<
  JSX.IntrinsicElements['input'] & { maskProps: MaskProps }
> = (props) => {
  const { maskProps, ...inputProps } = props;

  const { mask, definitions, placeholder } = maskProps || {};

  const maskInputRef = useMaskInput({
    mask,
    options: {
      definitions,
      placeholder,
      showMaskOnHover: false,
      showMaskOnFocus: false,
      jitMasking: true,
    },
  });

  const inputRef = useRef<HTMLInputElement>(null);

  useImperativeHandle(props.ref, () => inputRef.current!);
  useImperativeHandle(maskInputRef, () => inputRef.current!);

  return <input {...inputProps} ref={inputRef} />;
};

const BaseInput: FC<BaseInputProps> = forwardRef((props, ref) => {
  const {
    size = 'base',
    Icon,
    LeftIcon,
    RightIcon,
    removeLeftRounding,
    removeRightRounding,
    containerClassName,
    containerRef,
    renderExtraLeft,
    renderExtraRight,
    renderInput,
    iconClassName,
    innerClassName,
    aiTooltip,
    extendInputClassNames,
    extendContainerClassNames,
    children,
    maskProps,
    ...inputProps
  } = props;

  const [isFocused, setIsFocused] = useState(false);
  const hasValue = !!inputProps.value;

  const inputRef = useRef<HTMLInputElement>(null);

  const LeftIconComponent = LeftIcon || Icon;
  const RightIconComponent = RightIcon;

  const state = {
    isFocused,
    isDisabled: !!inputProps.disabled,
    hasValue,
    hasError: !!inputProps.error,
    size,
  };

  const inputPaddingClasses = useMemo(() => {
    let map = outerPaddingClasses[size];
    if (LeftIconComponent) {
      map = { ...map, l: leftPaddingForInputWithIcon[size] };
    }
    if (RightIconComponent) {
      map = { ...map, r: rightPaddingForInputWithIcon[size] };
    }
    return Object.values(map).join(' ');
  }, [size, !!LeftIconComponent, !!RightIconComponent]);

  const inputStateClasses = getInputStateClasses({
    state,
    getClassNames: extendInputClassNames,
  });

  const containerStateClasses = getContainerStateClasses({
    state,
    useAIColorScheme: aiTooltip?.enabled,
    getClassNames: extendContainerClassNames,
  });

  const innerContainerRef = useRef<HTMLDivElement>(null);

  useImperativeHandle(containerRef, () => innerContainerRef.current);

  const roundingClasses = {
    'rounded-l-[0.5rem]': !removeLeftRounding,
    'rounded-r-[0.5rem]': !removeRightRounding,
  };

  const commonInputProps = {
    ...inputProps,
    ref,
    className: classNames(
      props.className,
      inputPaddingClasses,
      inputStateClasses,
      roundingClasses,
    ),
    onFocus: (e: any) => {
      setIsFocused(true);
      inputProps.onFocus?.(e);
    },
    onBlur: (e: any) => {
      setIsFocused(false);
      inputProps.onBlur?.(e);
    },
  };

  useImperativeHandle(commonInputProps.ref, () => inputRef.current);

  const extraContentPaddingClasses = Object.values(
    outerPaddingClasses[size],
  ).join(' ');

  return (
    <PreFilledByAITooltip
      enabled={!state.isDisabled && aiTooltip?.enabled}
      hide={isFocused}
      description={aiTooltip?.description}
    >
      <div
        ref={innerContainerRef}
        className={classNames(
          'flex min-w-0',
          containerClassName,
          roundingClasses,
          containerStateClasses,
        )}
      >
        {renderExtraLeft?.(extraContentPaddingClasses)}
        <div className={classNames('relative flex flex-1 min-w-0')}>
          {!!LeftIconComponent && (
            <div
              className={classNames(
                'h-[1.5rem] w-[1.5rem] overflow-hidden pointer-events-none absolute top-1/2 -translate-y-1/2',
                leftIndentForIcons[size],
              )}
            >
              <span
                className={classNames('abs-center', getIconStateClasses(state))}
              >
                <LeftIconComponent
                  className={classNames(
                    iconClassName,
                    'h-[1.5rem] w-[1.5rem] transition-colors',
                  )}
                />
              </span>
            </div>
          )}
          {(() => {
            if (renderInput) {
              return renderInput({
                className: commonInputProps.className,
                onFocus: commonInputProps.onFocus,
                onBlur: commonInputProps.onBlur,
              } as BaseInputRenderProps);
            }
            if (maskProps?.mask) {
              return (
                <MaskedInput
                  {...commonInputProps}
                  maskProps={maskProps}
                  ref={inputRef}
                />
              );
            }
            if (commonInputProps.type === 'textarea') {
              return (
                <textarea
                  {...(commonInputProps as any)}
                  ref={inputRef as any}
                />
              );
            }
            return <input {...commonInputProps} ref={inputRef} />;
          })()}
          {!!RightIconComponent && (
            <div
              className={classNames(
                'h-[1.5rem] w-[1.5rem] overflow-hidden pointer-events-none absolute top-1/2 -translate-y-1/2',
                rightIndentForIcons[size],
              )}
            >
              <span
                className={classNames('abs-center', getIconStateClasses(state))}
              >
                <RightIconComponent
                  className={classNames(
                    iconClassName,
                    'h-[1.5rem] w-[1.5rem] transition-colors',
                  )}
                />
              </span>
            </div>
          )}
        </div>
        {renderExtraRight?.(extraContentPaddingClasses)}
      </div>
    </PreFilledByAITooltip>
  );
});

BaseInput.displayName = 'BaseInput';
export default BaseInput;
